import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize, Minimize, Loader2, Volume2, VolumeX, MousePointer2 } from "lucide-react";
import useFullscreen from "@/hooks/useFullscreen";

// Viewer for wide / partial panoramas (e.g. ~180 degree interior sweeps).
// Natural horizontal drag-to-pan of the full-height image — no forced 360 wrap, no distortion.
const WidePanoViewer = ({ tour, tours = [], onClose, onSelectTour, soundOn = true, onToggleSound }) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const { isFullscreen, toggleFullscreen } = useFullscreen(containerRef);
  const [showHint, setShowHint] = useState(true);
  const [x, setX] = useState(0);
  const boundsRef = useRef({ min: 0, max: 0 });
  const dragRef = useRef({ active: false, startX: 0, startTx: 0 });

  useEffect(() => {
    setLoading(true);
    setShowHint(true);
  }, [tour.id]);

  const clamp = (v) => Math.max(boundsRef.current.min, Math.min(0, v));

  const recomputeBounds = useCallback((centre = false) => {
    const c = containerRef.current;
    const img = imgRef.current;
    if (!c || !img || !img.naturalWidth) return;
    const displayedW = c.clientHeight * (img.naturalWidth / img.naturalHeight);
    const min = Math.min(0, c.clientWidth - displayedW);
    boundsRef.current = { min, max: 0 };
    setX((prev) => (centre ? min / 2 : clamp(prev)));
  }, []);

  const onImgLoad = () => {
    setLoading(false);
    recomputeBounds(true);
  };

  useEffect(() => {
    const onResize = () => recomputeBounds(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [recomputeBounds]);

  const onPointerDown = (e) => {
    dragRef.current = { active: true, startX: e.clientX, startTx: x };
    setShowHint(false);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    setX(clamp(dragRef.current.startTx + (e.clientX - dragRef.current.startX)));
  };
  const endDrag = () => {
    dragRef.current.active = false;
  };
  const onWheel = (e) => {
    setX((prev) => clamp(prev - e.deltaY));
    setShowHint(false);
  };

  useEffect(() => {
    const onFsChange = () => setTimeout(() => recomputeBounds(false), 60);
    document.addEventListener("fullscreenchange", onFsChange);
    const onKey = (e) => {
      if (e.key === "Escape" && !document.fullscreenElement) onClose();
    };
    window.addEventListener("keydown", onKey);
    const t = setTimeout(() => setShowHint(false), 4000);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [onClose, recomputeBounds]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-50 bg-black"
      data-testid="wide-pano-viewer"
    >
      <div
        ref={containerRef}
        className="absolute inset-0 overflow-hidden touch-none select-none viewer-canvas"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onWheel={onWheel}
        data-testid="wide-pano-surface"
      >
        <img
          ref={imgRef}
          src={tour.video_url}
          alt={tour.title}
          draggable={false}
          onLoad={onImgLoad}
          className="h-full w-auto max-w-none pointer-events-none"
          style={{ transform: `translateX(${x}px)`, willChange: "transform" }}
          data-testid="wide-pano-img"
        />
      </div>

      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#04271B] z-20" data-testid="wide-pano-loading">
          <Loader2 className="w-8 h-8 text-[#D6B15E] animate-spin" strokeWidth={1.5} />
          <p className="mt-4 text-xs tracking-[0.2em] uppercase text-[#A9B8A6]">Loading panorama</p>
        </div>
      )}

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between p-6">
        <div className="backdrop-blur-2xl bg-black/40 border border-white/10 px-5 py-3 rounded-full">
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#D6B15E]">{tour.room}</p>
          <p className="font-heading text-lg leading-tight">{tour.title}</p>
        </div>
        <button
          onClick={onClose}
          data-testid="wide-pano-close-btn"
          className="w-12 h-12 flex items-center justify-center backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      {/* Drag hint */}
      <AnimatePresence>
        {showHint && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
          >
            <div className="flex flex-col items-center gap-3 backdrop-blur-md bg-black/30 border border-white/10 px-8 py-6 rounded-2xl">
              <MousePointer2 className="w-8 h-8 text-white animate-hint-sweep" strokeWidth={1.5} />
              <p className="text-sm tracking-[0.15em] uppercase text-white/90">Drag to look around</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 z-30 p-6 flex flex-col items-center gap-4">
        {tours.length > 1 && (
          <div className="flex gap-3 max-w-full overflow-x-auto pb-1">
            {tours.map((t) => (
              <button
                key={t.id}
                onClick={() => onSelectTour(t)}
                data-testid={`wide-pano-scene-${t.id}`}
                className={`shrink-0 px-4 py-2 rounded-full text-xs tracking-widest uppercase border transition-colors backdrop-blur-2xl ${
                  t.id === tour.id ? "bg-[#D6B15E] text-black border-[#D6B15E]" : "bg-black/40 text-white border-white/10 hover:bg-white/10"
                }`}
              >
                {t.room}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleSound}
            data-testid="wide-pano-mute-toggle"
            title={soundOn ? "Mute ambient sound" : "Play ambient sound"}
            className="backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full w-12 h-12 flex items-center justify-center hover:bg-white/10 transition-colors"
            aria-label={soundOn ? "Mute ambient sound" : "Play ambient sound"}
          >
            {soundOn ? <Volume2 className="w-5 h-5 text-[#D6B15E]" strokeWidth={1.5} /> : <VolumeX className="w-5 h-5" strokeWidth={1.5} />}
          </button>
          <button
            onClick={toggleFullscreen}
            data-testid="wide-pano-fullscreen-toggle"
            className="backdrop-blur-2xl bg-black/40 border border-white/10 rounded-full px-6 py-3 flex items-center gap-3 hover:bg-white/10 transition-colors text-sm tracking-widest uppercase"
          >
            {isFullscreen ? <Minimize className="w-4 h-4" strokeWidth={1.5} /> : <Maximize className="w-4 h-4" strokeWidth={1.5} />}
            Fullscreen
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default WidePanoViewer;
